import React, { useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Stack, Typography, Avatar, Tooltip, IconButton, Divider,
} from '@mui/material';
import HomeRoundedIcon       from '@mui/icons-material/HomeRounded';
import AnalyticsRoundedIcon  from '@mui/icons-material/AnalyticsRounded';
import BuildRoundedIcon      from '@mui/icons-material/BuildRounded';
import TuneRoundedIcon       from '@mui/icons-material/TuneRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import BookRoundedIcon       from '@mui/icons-material/BookRounded';
import SettingsRoundedIcon   from '@mui/icons-material/SettingsRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import LogoutRoundedIcon     from '@mui/icons-material/LogoutRounded';
import EcoRoundedIcon        from '@mui/icons-material/SpaRounded';
import { AuthContext } from '../../components/signin/AuthContext';
import GrowSetupSelect from './GrowSetupSelect';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';

const EXPANDED_W = 230;
const COLLAPSED_W = 68;

const NAV = [
  { label: 'Dashboard',        icon: HomeRoundedIcon,           path: '/v3/dashboard'    },
  { label: 'Analytics',        icon: AnalyticsRoundedIcon,      path: '/v3/analytics'    },
  { label: 'Konfiguration',    icon: BuildRoundedIcon,          path: '/v3/config'       },
  { label: 'Aktoren',          icon: TuneRoundedIcon,           path: '/v3/actorconfig'  },
  { label: 'Benachrichtigungen', icon: NotificationsRoundedIcon, path: '/v3/alertconfig' },
  { label: 'Grow Plan',        icon: BookRoundedIcon,           path: '/v3/grow_plan'    },
  { label: 'Einstellungen',    icon: SettingsRoundedIcon,       path: '/v3/settings'     },
];

const pulseKf = `
@keyframes sidebarPulse {
  0%,100% { box-shadow: 0 0 0 0 rgba(118,196,66,0.5); }
  50%      { box-shadow: 0 0 0 6px rgba(118,196,66,0); }
}`;

export default function Sidebar() {
  const [collapsed, setCollapsed]   = useState(false);
  const { username, email, avatar, setAvatar, setToken } = useContext(AuthContext);
  const navigate  = useNavigate();
  const location  = useLocation();
  const W = collapsed ? COLLAPSED_W : EXPANDED_W;

  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'image/*': [] },
    onDrop: (files) => {
      const reader = new FileReader();
      reader.onload = () => {
        const b64 = reader.result;
        setAvatar(b64);
        axios.post('/upload_avatar', { base64String: b64 }).catch(console.error);
      };
      reader.readAsDataURL(files[0]);
    },
  });

  const handleLogout = () => {
    setToken(null);
    navigate('/');
  };

  return (
    <>
      <style>{pulseKf}</style>
      <Box
        sx={{
          width: W,
          minWidth: W,
          height: '100vh',
          position: 'sticky',
          top: 0,
          display: 'flex',
          flexDirection: 'column',
          background: 'rgba(4,14,5,0.92)',
          borderRight: '1px solid rgba(118,196,66,0.1)',
          backdropFilter: 'blur(20px)',
          transition: 'width 0.28s cubic-bezier(0.4,0,0.2,1), min-width 0.28s cubic-bezier(0.4,0,0.2,1)',
          overflow: 'hidden',
          zIndex: 100,
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            px: collapsed ? 1.5 : 2.5,
            borderBottom: '1px solid rgba(118,196,66,0.1)',
            gap: 1.5,
            transition: 'padding 0.28s',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: 36, height: 36, borderRadius: '10px',
              background: 'linear-gradient(135deg,#76c442 0%,#3a7d20 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 4px 14px rgba(118,196,66,0.35)',
            }}
          >
            <EcoRoundedIcon sx={{ color: '#fff', fontSize: 20 }} />
          </Box>
          {!collapsed && (
            <Box sx={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#76c442', lineHeight: 1.1 }}>
                Growr
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', lineHeight: 1 }}>
                v3
              </Typography>
            </Box>
          )}
        </Box>

        {/* Grow Setup Selector */}
        <Box
          sx={{
            px: collapsed ? 1 : 1.5,
            py: 1.5,
            borderBottom: '1px solid rgba(118,196,66,0.1)',
            flexShrink: 0,
          }}
        >
          <GrowSetupSelect collapsed={collapsed} />
        </Box>

        {/* Navigation */}
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1, px: collapsed ? 0.75 : 1 }}>
          <Stack spacing={0.5}>
            {NAV.map(({ label, icon: Icon, path }) => {
              const active = location.pathname === path || location.pathname.startsWith(path + '/');
              return (
                <Tooltip
                  key={path}
                  title={collapsed ? label : ''}
                  placement="right"
                  arrow
                >
                  <Box
                    onClick={() => navigate(path)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: collapsed ? 1.5 : 2,
                      py: 1.1,
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.18s ease',
                      background: active
                        ? 'linear-gradient(90deg,rgba(118,196,66,0.18) 0%,rgba(118,196,66,0.06) 100%)'
                        : 'transparent',
                      borderLeft: active ? '3px solid #76c442' : '3px solid transparent',
                      color: active ? '#76c442' : 'rgba(255,255,255,0.6)',
                      '&:hover': {
                        background: active
                          ? 'linear-gradient(90deg,rgba(118,196,66,0.22) 0%,rgba(118,196,66,0.08) 100%)'
                          : 'rgba(255,255,255,0.05)',
                        color: active ? '#76c442' : 'rgba(255,255,255,0.9)',
                        transform: 'translateX(2px)',
                      },
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: 20,
                        color: 'inherit',
                        flexShrink: 0,
                        filter: active ? 'drop-shadow(0 0 6px rgba(118,196,66,0.7))' : 'none',
                        transition: 'filter 0.18s ease',
                      }}
                    />
                    {!collapsed && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: active ? 700 : 500,
                          color: 'inherit',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          transition: 'opacity 0.2s',
                        }}
                      >
                        {label}
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </Stack>
        </Box>

        <Divider sx={{ borderColor: 'rgba(118,196,66,0.1)' }} />

        {/* User area */}
        <Box
          sx={{
            px: collapsed ? 1 : 1.5,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <Box
            {...getRootProps()}
            sx={{
              cursor: 'pointer',
              borderRadius: '50%',
              position: 'relative',
              flexShrink: 0,
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 1, right: 1,
                width: 9, height: 9,
                borderRadius: '50%',
                background: '#22c55e',
                border: '2px solid rgba(4,14,5,0.9)',
                animation: 'sidebarPulse 2s infinite',
              },
            }}
          >
            <input {...getInputProps()} />
            <Avatar
              src={avatar}
              alt={username}
              sx={{ width: 36, height: 36, border: '2px solid rgba(118,196,66,0.3)' }}
            />
          </Box>

          {!collapsed && (
            <>
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.9)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {username || 'Grower'}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}
                >
                  {email}
                </Typography>
              </Box>
              <Tooltip title="Logout" placement="top">
                <IconButton size="small" onClick={handleLogout} sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#ef4444' } }}>
                  <LogoutRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>

        {/* Collapse toggle */}
        <Box
          sx={{
            px: collapsed ? 1 : 1.5,
            pb: 1.5,
            display: 'flex',
            justifyContent: collapsed ? 'center' : 'flex-end',
            flexShrink: 0,
          }}
        >
          <Tooltip title={collapsed ? 'Expand' : 'Collapse'} placement="right">
            <IconButton
              size="small"
              onClick={() => setCollapsed(p => !p)}
              sx={{
                background: 'rgba(118,196,66,0.1)',
                border: '1px solid rgba(118,196,66,0.2)',
                color: '#76c442',
                '&:hover': { background: 'rgba(118,196,66,0.18)', border: '1px solid #76c442' },
                transition: 'all 0.18s ease',
              }}
            >
              {collapsed ? <ChevronRightRoundedIcon fontSize="small" /> : <ChevronLeftRoundedIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </>
  );
}
